import { getClientCookie, COOKIE } from "#shared/utils/cookie";

const logSyle =
  "background: #2E495E;border-radius: 0.5em;color: white;font-weight: bold;padding: 2px 0.5em;";

export const Tiktok = {
  options: {
    id: null as null | string | number,
    debug: false,
    pageSlug: '',
    countryCode: ''
  },

  log(...args: any[]) {
    if (this.options.debug && !import.meta.server) {
      console.log("%cTiktok Pixel", logSyle, ...args);
    }
  },

  getSessionId() {
    return getClientCookie(COOKIE.SESSION_ID) || ''
  },

  event(eventName: any, params?: any, thirdArgs?: Record<string, any>, ...restOfArgs: any[]) {
    if (!this.options.id) return;
    const sessionId = this.getSessionId();
    const thirdArgsObj = { ...(thirdArgs || {}), event_id: sessionId };
    const argList = [eventName, params, thirdArgsObj, ...restOfArgs];
    (globalThis as any)?.ttq?.track?.(...argList);
    this.log("event", argList);
  },

  track(...args: any[]) {
    if (!this.options.id) return
    this.event(...(args as Parameters<typeof this.event>));
  },

  purchase(payload: {
    value: any;
    currency: any;
    description: string;
    content_id: string;
    orderID: string;
  }) {
    this.event('Purchase', {
      quantity: 1,
      content_type: 'product',
      ...payload
    });
  },

  init(payload: { pixelId: number | string; debug?: boolean; pageSlug: string; countryCode: string }) {
    const headScripts = {
      script: [] as any[],
      noscript: [] as any[]
    };

    if (import.meta.server) return headScripts;
    if (this.options.id) return headScripts;
    if (!payload.pixelId) {
      console.warn("Tiktok Pixel cannot be installed because there is no Pixel ID!");
      return headScripts;
    }

    this.options.id = payload.pixelId;
    this.options.debug = !!payload.debug;
    this.options.pageSlug = payload.pageSlug;
    this.options.countryCode = payload.countryCode;

    if (this.options.debug) {
      this.track('load', this.options.id);
      this.track('page');
      return headScripts;
    }

    if (globalThis.document) {
      (function (w: any, d: any, t: any, id: any, sid: string) {
        w.TiktokAnalyticsObject=t;
        var ttq=w[t]=w[t]||[];
        ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],
        ttq.setAndDefer=function(t: any,e: any){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
        for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
        ttq.instance=function(t: any){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},
        ttq.load=function(e: any,n: any){
          var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},
          ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,
          o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a?.parentNode?.insertBefore(o,a);
          var win = globalThis as any;
          if (!win?.__EMITTER__) return false;
          win?.__EMITTER__.emit('tiktokLoad');
          if (win) {
            win.loadedIntegrations=win.loadedIntegrations||[];
            win.loadedIntegrations.push('Tiktok');
          }
        };
        ttq.load(id, { historyObserver: false });
        ttq.page({ event_id: sid });
      }(globalThis, document, 'ttq', this.options.id, this.getSessionId()));
    }

    return headScripts
  },

  initOnSSR(payload: { pixelId: number | string; debug?: boolean, pageSlug: string; countryCode: string; }) {
    const headScripts = {
      script: [] as any[],
      noscript: [] as any[]
    };

    if (!payload.pixelId) {
      console.warn("Tiktok Pixel cannot be installed because there is no Pixel ID!");
      return headScripts;
    }

    this.options.id = payload.pixelId;
    this.options.debug = !!payload.debug;
    this.options.pageSlug = payload.pageSlug;
    this.options.countryCode = payload.countryCode;

    if (this.options.debug) {
      this.track('load', this.options.id);
      this.track('page');
      return headScripts;
    }

    if (import.meta.client) {
      const win = globalThis as any;
      /*
        Trigger tiktok load with proxy object.
        This is necessary because script load on SSR and till then, window.__EMITTER__ is not set to window object yet.
        When load this initOnSSR script, check proxy object on window.
        If object found, that means script loaded.
        After that, set object as true to trigger event by proxy handler below
      */
      if (win?.proxy2) win.proxy2.tiktokload = true; // Trigger listener
    }

    headScripts.script.push({
      innerHTML: `!function (w, d, t) {
      w.TiktokAnalyticsObject=t;
      var ttq=w[t]=w[t]||[];
      ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],
      ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
      for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
      ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},
      ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,
      ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",
      o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a);
      w.loadedIntegrations=w.loadedIntegrations||[];
      w.loadedIntegrations.push('Tiktok');
      window.proxy2 = new Proxy({ tiktokload: false },{
      set(obj, prop, value) {obj[prop] = value;
      if(window.__EMITTER__) {window.__EMITTER__.emit("tiktokLoad", value);}return true;}});};
      ttq.load('${this.options.id}', { historyObserver: false });
      ttq.page({ event_id: '${this.getSessionId()}' });
      }(window, document, 'ttq');`,
    });

    return headScripts;
  }
}
